import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Clock, 
  TrendingUp, 
  AlertCircle,
  CheckCircle,
  XCircle,
  ArrowUp,
  Shield,
  Info
} from 'lucide-react';
import { useSlaAccess } from '../../utils/slaAccessControl';
import SlaBadge, { SlaStatusIndicator } from './SlaBadge';
import apiClient from '../../utils/apiClient';
import { BUSINESS_CLAIM_TYPES, getBusinessClaimTypesArray, getBusinessClaimType } from '../../constants/businessClaimTypes';
import OmCircularProgress from '../ui/OmCircularProgress';
import OmPagination from '../ui/OmPagination';
import SlaTableSkeleton from './SlaTableSkeleton';
import BackButton from '../ui/BackButton';

/**
 * Get display name for claim type code
 * @param {string} typeCode - Business claim type code
 * @returns {string} Display name
 */
const getTypeDisplayName = (typeCode) => {
  const type = getBusinessClaimType(typeCode);
  return type ? type.displayName : typeCode;
};

/**
 * Get escalation badge component
 * @param {number} escalationLevel - Escalation level (0-3)
 * @returns {JSX.Element|null} Badge component or null
 */
const getEscalationBadge = (escalationLevel) => {
  if (!escalationLevel || escalationLevel === 0) return null;

  const configs = {
    1: {
      label: 'L1',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
      borderColor: 'border-yellow-300'
    },
    2: {
      label: 'L2',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
      borderColor: 'border-orange-300'
    },
    3: {
      label: 'L3',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
      borderColor: 'border-red-300'
    }
  };

  const config = configs[escalationLevel];
  if (!config) return null;

  return (
    <span 
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}
      title={`Escalation Level ${escalationLevel}`}
    >
      {config.label}
    </span>
  );
};

/**
 * Get time overdue display component
 * @param {number} hoursOverdue - Hours overdue
 * @returns {JSX.Element|null} Time overdue display or null
 */
const getTimeOverdueDisplay = (hoursOverdue) => {
  if (!hoursOverdue || hoursOverdue <= 0) return null;

  return (
    <span className="text-xs text-red-600 font-medium">
      {hoursOverdue}h overdue
    </span>
  );
};

/**
 * SLA Dashboard Component
 * 
 * Displays comprehensive SLA monitoring and compliance metrics
 * Based on user story requirements for SLA tracking and escalation
 * 
 * Access Control (from user stories):
 * - "Role-based access: Claims Officer, Approver, Finance"
 * - "SLA tracking with visual dashboards"
 */
const SlaDashboard = ({ user }) => {
  const [slaOverview, setSlaOverview] = useState(null);
  const [slaMetrics, setSlaMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    status: '',
    type: ''
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });

  // Get SLA access control based on user role
  const slaAccess = useSlaAccess(user);

  const fetchSlaData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      let isTokenExpired = false;
      let tokenPayload = null;
      if (token) {
        try {
          tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          isTokenExpired = tokenPayload.exp && tokenPayload.exp < currentTime;
        } catch (_e) {
          isTokenExpired = true;
        }
      }
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      if (isTokenExpired) {
        throw new Error('Authentication token has expired. Please log in again.');
      }
      
      // Apply access control filters
      const userFilters = slaAccess.queryFilters || {};
      const mergedFilters = { 
        ...filter, 
        ...userFilters,
        page: currentPage,
        limit: itemsPerPage
      };
      
      // Fetch SLA overview and metrics in parallel
      const [overviewResponse, metricsResponse] = await Promise.all([
        apiClient.get(`sla-management/overview?${new URLSearchParams(mergedFilters)}`),
        // Only fetch metrics if user has permission
        slaAccess.canViewSlaMetrics 
          ? apiClient.get('monitoring/sla')
          : Promise.resolve({ data: null })
      ]);

      const overview = overviewResponse.data;
      const metrics = metricsResponse.data;

      setSlaOverview(overview.data);
      setSlaMetrics(metrics?.data || metrics);
      
      // Update pagination state from backend response
      if (overview.data?.pagination) {
        setPagination(overview.data.pagination);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching SLA data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlaData();
    
    // Refresh data every 5 minutes
    const interval = setInterval(fetchSlaData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [filter, currentPage, itemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Access control check - based on user stories: "Role-based access: Claims Officer, Approver, Finance"
  if (!slaAccess.canAccessDashboard) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center">
          <Shield className="w-6 h-6 text-yellow-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-yellow-800">Access Restricted</h3>
            <p className="text-yellow-700 mt-1">
              You don't have permission to access the SLA monitoring dashboard. 
              Access is limited to Claims Officers, Approvers, Finance team, and Administrators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const getSlaStatusColor = (status) => {
    switch (status) {
      case 'WITHIN_SLA': return 'text-green-600 bg-green-100';
      case 'APPROACHING_BREACH': return 'text-orange-600 bg-orange-100';
      case 'BREACHED': return 'text-red-600 bg-red-100';
      case 'ESCALATED_L1':
      case 'ESCALATED_L2':
      case 'ESCALATED_L3': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSlaStatusIcon = (status) => {
    switch (status) {
      case 'WITHIN_SLA': return <CheckCircle className="w-4 h-4" />;
      case 'APPROACHING_BREACH': return <AlertTriangle className="w-4 h-4" />;
      case 'BREACHED': return <XCircle className="w-4 h-4" />;
      case 'ESCALATED_L1':
      case 'ESCALATED_L2':
      case 'ESCALATED_L3': return <ArrowUp className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatTimeRemaining = (hours) => {
    if (!hours || hours <= 0) return 'Overdue';
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    if (days > 0) {
      return `${days}d ${remainingHours}h`;
    }
    return `${remainingHours}h`;
  };

  // Access control check - based on user stories: "Role-based access: Claims Officer, Approver, Finance"
  if (!slaAccess.canAccessDashboard) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center">
          <Shield className="w-6 h-6 text-yellow-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-yellow-800">Access Restricted</h3>
            <p className="text-yellow-700 mt-1">
              You don't have permission to access the SLA monitoring dashboard. 
              Access is limited to Claims Officers, Approvers, Finance team, and Administrators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Initial loading state - show skeleton
  if (loading && !slaOverview) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton />
        </div>
        
        <SlaTableSkeleton />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="mb-6">
          <BackButton />
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">Error loading SLA data: {error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="mb-6">
        <BackButton />
      </div>

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">SLA Monitoring Dashboard</h1>
            {/* Inline refresh loading indicator */}
            {loading && slaOverview && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <div className="w-4 h-4">
                  <OmCircularProgress indeterminate={true} size="small" />
                </div>
                <span>Updating...</span>
              </div>
            )}
          </div>
          <p className="text-gray-600">Service Level Agreement compliance and escalation tracking</p>
          {slaAccess.hasLimitedAccess && (
            <div className="flex items-center mt-2 text-sm text-orange-600">
              <Info className="w-4 h-4 mr-1" />
              Limited access: You can only view SLA data for claims you submitted
            </div>
          )}
        </div>
        
        <div className="flex space-x-2">
          {/* Manual check button removed */}
        </div>
      </div>

      {/* SLA Metrics Cards */}
      {slaMetrics && slaAccess.canViewSlaMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">SLA Compliance</p>
                <p className="text-2xl font-bold text-gray-900">
                  {slaMetrics.complianceRate?.toFixed(1) || 0}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Clock className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Resolution Time</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    const avgTime = slaMetrics.averageResponseTime;
                    if (!avgTime || typeof avgTime !== 'object') return '0';
                    const times = Object.values(avgTime).filter(t => typeof t === 'number');
                    if (times.length === 0) return '0';
                    const avg = times.reduce((sum, t) => sum + t, 0) / times.length;
                    return avg.toFixed(1);
                  })()}h
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Breach Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    const breaches = slaMetrics.currentBreaches || 0;
                    const total = slaMetrics.totalClaims || 1;
                    return ((breaches / total) * 100).toFixed(1);
                  })()}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <TrendingUp className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Escalation Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(() => {
                    const escalated = slaOverview?.summary?.escalated || 0;
                    const total = slaOverview?.summary?.total || 1;
                    return ((escalated / total) * 100).toFixed(1);
                  })()}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {slaOverview && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Current SLA Status Summary</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{slaOverview.summary.total}</p>
              <p className="text-sm text-gray-600">Total Claims</p>
            </div>
            
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{slaOverview.summary.withinSla}</p>
              <p className="text-sm text-gray-600">Within SLA</p>
            </div>
            
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{slaOverview.summary.approaching}</p>
              <p className="text-sm text-gray-600">Approaching Breach</p>
            </div>
            
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{slaOverview.summary.breached}</p>
              <p className="text-sm text-gray-600">Breached</p>
            </div>
            
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{slaOverview.summary.escalated}</p>
              <p className="text-sm text-gray-600">Escalated</p>
              {slaOverview.summary.escalated > 0 && (() => {
                const l1 = slaOverview.claims.filter(c => c.escalationLevel === 1).length;
                const l2 = slaOverview.claims.filter(c => c.escalationLevel === 2).length;
                const l3 = slaOverview.claims.filter(c => c.escalationLevel === 3).length;
                return (l1 > 0 || l2 > 0 || l3 > 0) && (
                  <p className="text-xs text-gray-500 mt-1">
                    {l1 > 0 && <span className="text-yellow-600">L1: {l1}</span>}
                    {l1 > 0 && (l2 > 0 || l3 > 0) && <span className="mx-1">•</span>}
                    {l2 > 0 && <span className="text-orange-600">L2: {l2}</span>}
                    {l2 > 0 && l3 > 0 && <span className="mx-1">•</span>}
                    {l3 > 0 && <span className="text-red-600">L3: {l3}</span>}
                  </p>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SLA Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All Statuses</option>
              {slaAccess.hasFullAccess && (
                <>
                  <option value="WITHIN_SLA">Within SLA</option>
                  <option value="APPROACHING_BREACH">Approaching Breach</option>
                  <option value="BREACHED">Breached</option>
                  <option value="ESCALATED">Escalated</option>
                </>
              )}
              {slaAccess.hasLimitedAccess && (
                <>
                  <option value="WITHIN_SLA">Within SLA</option>
                  <option value="APPROACHING_BREACH">Approaching Breach</option>
                  <option value="BREACHED">Breached</option>
                </>
              )}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Claim Type</label>
            <select
              value={filter.type}
              onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All Types</option>
              {getBusinessClaimTypesArray().map(type => (
                <option key={type.code} value={type.code}>
                  {type.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Claims List */}
      {slaOverview && slaOverview.claims.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Claims with SLA Tracking
              {pagination.total > 0 && (
                <span className="text-sm text-gray-500 ml-2">
                  ({pagination.total} total)
                </span>
              )}
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Claim ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SLA Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time Remaining
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deadline
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                </tr>
              </thead>
              
              <tbody className="bg-white divide-y divide-gray-200">
                {slaOverview.claims.map((claim) => (
                  <tr key={`sla-table-${claim.id}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      <div className="flex items-center gap-2">
                        <a href={`/claims/${claim.id}`} className="hover:underline">
                          {claim.claimNumber || claim.id}
                        </a>
                        {getEscalationBadge(claim.escalationLevel)}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getTypeDisplayName(claim.type)}
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {claim.sla && (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSlaStatusColor(claim.sla.slaStatus)}`}>
                            {getSlaStatusIcon(claim.sla.slaStatus)}
                            <span className="ml-1">{claim.sla.slaStatus.replace('_', ' ')}</span>
                          </span>
                        )}
                        {claim.sla?.urgencyLevel && (
                          <span className="text-xs text-gray-600">
                            {claim.sla.urgencyLevel}
                          </span>
                        )}
                        {getTimeOverdueDisplay(claim.sla?.hoursOverdue)}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {claim.sla?.timeRemaining ? (
                        <span className={claim.sla.timeRemaining < 24 ? 'text-red-600 font-medium' : ''}>
                          {formatTimeRemaining(claim.sla.timeRemaining)}
                        </span>
                      ) : (
                        <span className="text-red-600 font-medium">
                          {claim.sla?.hoursOverdue ? `Overdue ${claim.sla.hoursOverdue}h` : 'N/A'}
                        </span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {claim.sla?.targetResolutionDate && (
                        new Date(claim.sla.targetResolutionDate).toLocaleString()
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {claim.assignedTo?.username || 'Unassigned'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Component with Items Per Page Selector */}
      {!loading && !error && slaOverview && pagination.total > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          {/* Top Section: Items per page selector */}
          <div className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <label htmlFor="sla-items-per-page" className="text-sm font-medium text-gray-600 whitespace-nowrap">
                Show:
              </label>
              <div className="relative">
                <select
                  id="sla-items-per-page"
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                  className="appearance-none pl-4 pr-10 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 transition-all duration-200 hover:border-blue-400 hover:shadow-sm cursor-pointer"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <span className="text-sm text-gray-600 whitespace-nowrap">per page</span>
            </div>
            
            {/* Page info */}
            <div className="text-sm text-gray-600">
              Page <span className="font-semibold text-gray-900">{pagination.page}</span> of{' '}
              <span className="font-semibold text-gray-900">{pagination.totalPages}</span>
            </div>
          </div>
          
          {/* Bottom Section: OmPagination component */}
          <OmPagination
            currentPage={pagination.page || currentPage}
            totalItems={pagination.total}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            showResultsInfo={true}
          />
        </div>
      )}

    </div>
  );
};

export default SlaDashboard;
